import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, PLP_LOGO } from '../App';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  LayoutDashboard,
  Megaphone,
  Calendar,
  Newspaper,
  Award,
  Building2,
  Users,
  MessageSquare,
  Wrench,
  LogOut,
  Menu,
  X,
  Home,
  ChevronRight,
  Shield,
  MapPin,
  Settings,
  ShieldCheck,
  Crown,
  Car,
  Bus,
  UserPlus,
  Mic,
  Video,
  BarChart3,
  Vote,
  Image,
  Bell,
  ImageIcon,
  FileText
} from 'lucide-react';

const sidebarItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/registrations', label: 'User Registrations', icon: UserPlus, highlight: true },
  { path: '/admin/site-content', label: 'Site Content', icon: FileText },
  { path: '/admin/popups', label: 'Custom Popups', icon: Bell },
  { path: '/admin/homepage-photos', label: 'Homepage Photos', icon: ImageIcon },
  { path: '/admin/streams', label: 'Live Streams', icon: Video },
  { path: '/admin/townhall', label: 'AI Town Hall', icon: Mic },
  { path: '/admin/polls', label: 'Community Polls', icon: Vote },
  { path: '/admin/gallery', label: 'Photo Gallery', icon: Image },
  { path: '/admin/announcements', label: 'Announcements', icon: Megaphone },
  { path: '/admin/news', label: 'Community News', icon: Newspaper },
  { path: '/admin/events', label: 'Events', icon: Calendar },
  { path: '/admin/spotlights', label: 'Spotlights', icon: Award },
  { path: '/admin/businesses', label: 'Businesses', icon: Building2 },
  { path: '/admin/resources', label: 'Resources', icon: MapPin },
  { path: '/admin/issues', label: 'Fix It Forward', icon: Wrench },
  { path: '/admin/safety-pins', label: 'Safety Reports', icon: Shield },
  { path: '/admin/volunteers', label: 'Volunteers', icon: Users },
  { path: '/admin/carpool-routes', label: 'Carpool Routes', icon: Bus },
  { path: '/admin/carpool-drivers', label: 'Carpool Drivers', icon: Car },
  { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { path: '/admin/admins', label: 'Admin Management', icon: ShieldCheck, superAdminOnly: true },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-plp-blue fixed inset-y-0 left-0 z-50">
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img src={PLP_LOGO} alt="PLP Logo" className="h-10 w-auto" />
            <div>
              <p className="font-heading font-bold text-white text-sm">Killarney Connect</p>
              <p className="text-xs text-white/60">Admin Portal</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {sidebarItems
              .filter(item => !item.superAdminOnly || user?.is_super_admin || user?.permissions?.manage_admins)
              .map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    active
                      ? 'bg-white text-plp-blue'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-3">
            <Home className="w-4 h-4" />
            Back to Site
          </Link>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              user?.is_super_admin ? 'bg-plp-gold' : 'bg-white/20'
            }`}>
              {user?.is_super_admin ? (
                <Crown className="w-5 h-5 text-plp-blue" />
              ) : (
                <span className="text-white font-bold">{user?.name?.charAt(0)}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                {user?.is_super_admin && (
                  <Badge className="bg-plp-gold text-plp-blue text-[10px] px-1.5 py-0">Super</Badge>
                )}
              </div>
              <p className="text-white/60 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full mt-3 text-white/70 hover:text-white hover:bg-white/10 justify-start"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-plp-blue z-50 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img src={PLP_LOGO} alt="PLP Logo" className="h-8 w-auto" />
          <span className="font-heading font-bold text-white text-sm">Admin</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-plp-blue transform transition-transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 mt-14">
          <nav className="space-y-1">
            {sidebarItems
              .filter(item => !item.superAdminOnly || user?.is_super_admin || user?.permissions?.manage_admins)
              .map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    active
                      ? 'bg-white text-plp-blue'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-6 pt-4 border-t border-white/10">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-3" onClick={() => setSidebarOpen(false)}>
              <Home className="w-4 h-4" />
              Back to Site
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        <div className="pt-14 lg:pt-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
